import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { apiGet, apiPost } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { applyPreprodChrome } from '../lib/preprodChrome';

interface FeaturesEnv {
  environment?: 'preprod';
  preprod_derniere_maj?: string | null;
  preprod_sync?: boolean;
}

interface SyncStatus {
  derniere_maj: string | null;
  etat: 'aucun' | 'demande' | 'en_cours' | 'essai' | 'reussi' | 'echec';
  etat_depuis: string | null;
  message: string | null;
  prochaine_demande_possible: string | null;
}

const ETAT_LABEL: Record<SyncStatus['etat'], string> = {
  aucun: '',
  demande: 'Synchronisation demandée, prise en charge dans la minute',
  en_cours: 'Synchronisation en cours',
  // « réussie » : copie réelle seulement ; un essai à blanc ne touche pas la base.
  essai: 'Essai à blanc, base non modifiée',
  reussi: 'Dernière synchronisation réussie',
  echec: 'Dernière synchronisation en échec',
};

function fmt(iso: string | null | undefined): string {
  if (!iso) return 'inconnue';
  const d = new Date(iso);
  return d.toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
}

/** Date courte pour un téléphone : « 28/10 14:05 ». */
function fmtShort(iso: string | null | undefined): string {
  if (!iso) return 'inconnue';
  return new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

/**
 * Bandeau « PRÉPROD », en haut de toutes les pages, seulement quand l'API
 * dit être la préprod (/api/features, NODE_ENV côté API ; jamais le nom
 * d'hôte). Donne la date de la dernière mise à jour de la base depuis la
 * prod. Pour un administrateur : bouton « Synchroniser avec la prod », qui
 * DÉPOSE une demande, prise ensuite par un lanceur de l'hôte.
 */
export function PreprodBanner() {
  const { user } = useAuth();
  const [env, setEnv] = useState<FeaturesEnv | null>(null);
  const [sync, setSync] = useState<SyncStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    void apiGet<FeaturesEnv>('/api/features').then(setEnv).catch(() => {});
  }, []);

  const canSync = env?.environment === 'preprod' && env.preprod_sync === true && user?.is_admin === true;

  const loadSync = useCallback(async () => {
    try {
      setSync(await apiGet<SyncStatus>('/api/preprod/sync'));
    } catch {
      setSync(null);
    }
  }, []);

  useEffect(() => {
    if (!canSync) return;
    void loadSync();
  }, [canSync, loadSync]);

  // Demande en attente ou en cours : on suit son état.
  const pending = sync?.etat === 'demande' || sync?.etat === 'en_cours';
  useEffect(() => {
    if (!canSync || !pending) return;
    const t = setInterval(() => void loadSync(), 15_000);
    return () => clearInterval(t);
  }, [canSync, pending, loadSync]);

  const isPreprod = env?.environment === 'preprod';

  // Onglet : favicon rayé et titre « [PRÉPROD] … ».
  useEffect(() => {
    if (!isPreprod) return;
    return applyPreprodChrome(document);
  }, [isPreprod]);

  // Le bandeau est fixe en haut : sa hauteur réelle (il peut passer sur deux
  // lignes) est publiée dans --preprod-h, que suivent la cale sous lui,
  // l'en-tête collant, le menu et scroll-padding-top (ancres, focus clavier).
  const bannerRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const el = bannerRef.current;
    if (!isPreprod || !el) return;
    const root = document.documentElement;
    const publish = () => root.style.setProperty('--preprod-h', `${el.offsetHeight}px`);
    publish();
    const ro = new ResizeObserver(publish);
    ro.observe(el);
    return () => {
      ro.disconnect();
      root.style.removeProperty('--preprod-h');
    };
  }, [isPreprod]);

  if (!isPreprod || !env) return null;

  const request = async () => {
    const ok = window.confirm(
      'Synchroniser la préprod avec la prod ?\n\nCela REMPLACE TOUTE LA BASE DE PRÉPROD par une copie anonymisée de la prod et DÉCONNECTE TOUT LE MONDE. Les données saisies en préprod sont perdues.',
    );
    if (!ok) return;
    setBusy(true);
    setErr(null);
    try {
      await apiPost('/api/preprod/sync', { confirmation: 'REMPLACER_LA_BASE_DE_PREPROD' });
      await loadSync();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Demande refusée');
    } finally {
      setBusy(false);
    }
  };

  const derniereMaj = sync?.derniere_maj ?? env.preprod_derniere_maj ?? null;
  const blocked = pending || Boolean(sync?.prochaine_demande_possible);

  const etatLabel =
    canSync && sync && sync.etat !== 'aucun'
      ? `${ETAT_LABEL[sync.etat]}${sync.etat_depuis ? ` (${fmt(sync.etat_depuis)})` : ''}${
          sync.message && sync.message !== ETAT_LABEL[sync.etat] ? ` : ${sync.message}` : ''
        }`
      : null;
  const syncTitle = sync?.prochaine_demande_possible
    ? `Prochaine demande possible à ${fmt(sync.prochaine_demande_possible)}`
    : 'Remplace toute la base de préprod, déconnecte tout le monde';

  return (
    <>
      <div ref={bannerRef} className="preprod-banner" role="status">
        <strong className="preprod-banner-tag">PRÉPROD</strong>
        <span className="preprod-banner-long">Base mise à jour depuis la prod le {fmt(derniereMaj)}</span>
        <span className="preprod-banner-short">Base du {fmtShort(derniereMaj)}</span>
        {etatLabel && <span className="preprod-banner-state">· {etatLabel}</span>}
        {canSync && (
          <button
            type="button"
            className="btn preprod-banner-btn"
            onClick={() => void request()}
            disabled={busy || blocked}
            aria-label={busy ? 'Demande en cours' : 'Synchroniser avec la prod'}
            title={etatLabel ? `${etatLabel}. ${syncTitle}` : syncTitle}
          >
            <span className="preprod-banner-long">{busy ? 'Demande…' : 'Synchroniser avec la prod'}</span>
            <span className="preprod-banner-short" aria-hidden="true">
              ⟳
            </span>
          </button>
        )}
        {err && <span className="preprod-banner-err">{err}</span>}
      </div>
      {/* Cale de la hauteur du bandeau fixe : rien ne passe dessous. */}
      <div className="preprod-banner-spacer" aria-hidden="true" />
    </>
  );
}
